import DatePickerModel from '@data/models/DatePicker';
import DatePicker from '@molecules/BEDatePicker';
import BEText from '@molecules/Text';
import BEIcon from '@molecules/BEIcon';
import BEButton, { BELoadingButton } from '@molecules/Button/Button';
import { useCallback, useMemo, useState } from 'react';
import { PromoCodeProps } from './PromoCodeProps';
import {
  StyledPromoCode,
  StyledPromoCodeContainer,
  StyledVerticalPromoCode,
} from './StyledPromoCode';
import { useStore } from '@data/other/withStore';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';

export const BasicPromoCode = ({
  layout = 'horizontal',
  checkin,
  checkout,
  code = '',
  size = 'lg',
  calendarIcon = true,
  onChangeDates,
  onClosePopover,
  onChangeCode,
  onClearPromoCode,
  onClickSearch,
}: PromoCodeProps) => {
  const { t } = useTranslation('common');

  const [containerRef, setContainerRef] = useState<any>();
  const [showPromoCode, setShowPromoCode] = useState<boolean>(false);
  const [isFetchingAccomodations, setIsFetchingAccomodations] = useState(false);
  const { components } = useStore();

  const checkinPlaceholder = useMemo(() => {
    if (!checkin && !checkout) return t('check-in');
    return '-';
  }, [checkin, checkout, t]);

  const checkoutPlaceholder = useMemo(() => {
    if (!checkin && !checkout) return t('check-out');
    return '-';
  }, [checkin, checkout, t]);

  const RenderedComponent = useMemo(() => {
    if (layout === 'horizontal') {
      return (
        <StyledPromoCodeContainer size={size} ref={setContainerRef}>
          <DatePicker
            checkin={checkin}
            checkout={checkout}
            size={size}
            calendarIcon={calendarIcon}
            border={false}
            parentRef={containerRef}
            onChangeDates={onChangeDates}
            onClosePopover={onClosePopover}
            isFetchingAccomodations={isFetchingAccomodations}
            defaults={components.promoCode.value}
          />
          <div className="verticalSeparator" />
          <StyledPromoCode size={size}>
            <input
              placeholder="Promocode"
              onChange={(e) => onChangeCode?.(e.target.value)}
              value={code}
            ></input>
            {size !== 'sm' && code.length > 0 && (
              <BEIcon
                name="close"
                className="clear-promo"
                onClick={onClearPromoCode}
              />
            )}
          </StyledPromoCode>
          <BELoadingButton
            onBeforeClick={() => setIsFetchingAccomodations(true)}
            onAfterClick={() => setIsFetchingAccomodations(false)}
            isDisabled={!checkin || !checkout}
            color="primary"
            minWidth="40px"
            width="40px"
            icon="magnify"
            onClick={() => onClickSearch?.(checkin, checkout, code)}
          />
        </StyledPromoCodeContainer>
      );
    } else if (layout === 'vertical') {
      return (
        <StyledVerticalPromoCode>
          <div className="box header">
            <BEText type="h3">{t('select-stay-dates')}</BEText>
          </div>
          <div className="box main-content">
            <DatePicker
              checkin={checkin}
              checkout={checkout}
              onChangeDates={onChangeDates}
              onClosePopover={onClosePopover}
              size="lg"
              isFetchingAccomodations={isFetchingAccomodations}
              checkinPlaceholder={checkinPlaceholder}
              checkoutPlaceholder={checkoutPlaceholder}
            />
            {showPromoCode || code.length > 0 ? (
              <div className="promo-code-input">
                <input
                  placeholder={t('promocode') as string}
                  autoFocus={true}
                  onChange={(e) => onChangeCode?.(e.target.value)}
                  value={code}
                />
                <BEIcon className="icon icon-left" size={'lg'} name={'tag'} />
                <BEIcon
                  className="icon icon-right close"
                  size={'lg'}
                  name={'close'}
                  onClick={onClearPromoCode}
                />
              </div>
            ) : (
              <BEButton
                size="lg"
                variant="ghost"
                onClick={() => setShowPromoCode(true)}
              >
                + Promocode / Group Code
              </BEButton>
            )}
          </div>
          <div className="box footer">
            <BELoadingButton
              onBeforeClick={() => setIsFetchingAccomodations(true)}
              onAfterClick={() => setIsFetchingAccomodations(false)}
              onClick={() => onClickSearch?.(checkin, checkout, code)}
              isDisabled={!checkin || !checkout}
              color="primary"
              size="lg"
            >
              {t('search')}
            </BELoadingButton>
          </div>
        </StyledVerticalPromoCode>
      );
    }
  }, [
    layout,
    size,
    checkin,
    checkout,
    calendarIcon,
    containerRef,
    onChangeDates,
    onClosePopover,
    isFetchingAccomodations,
    components.promoCode.value,
    code,
    onClearPromoCode,
    onChangeCode,
    onClickSearch,
    t,
    checkinPlaceholder,
    checkoutPlaceholder,
    showPromoCode,
  ]);

  return <>{RenderedComponent}</>;
};

export type PromoCodeValue = {
  checkin?: Date;
  checkout?: Date;
  code?: string;
};

export const PromoCode = ({
  size = 'lg',
  calendarIcon = true,
  layout = 'horizontal',
  onClickSearch,
}: PromoCodeProps) => {
  const { components } = useStore();
  const [datePickerValue, setDatePickerValue] = useState<PromoCodeValue>({
    checkin:
      layout === 'horizontal' ? components.promoCode.value.checkin : undefined,
    checkout:
      layout === 'horizontal' ? components.promoCode.value.checkout : undefined,
    code: components.promoCode.value.code,
  });

  const onClosePopover = useCallback(() => {
    // todo: here is the place we should suggest a date
  }, []);

  const onChangeDates = useCallback(
    (dates: DatePickerModel) => {
      setDatePickerValue({
        ...datePickerValue,
        ...dates,
      });
    },
    [datePickerValue]
  );

  const onChangeCode = useCallback(
    (code: string) => {
      setDatePickerValue({
        ...datePickerValue,
        code,
      });
    },
    [datePickerValue]
  );

  const onClearPromoCode = useCallback(() => {
    setDatePickerValue({
      ...datePickerValue,
      code: '',
    });
  }, [datePickerValue]);

  return (
    <BasicPromoCode
      layout={layout}
      size={size}
      calendarIcon={calendarIcon}
      checkin={datePickerValue.checkin}
      checkout={datePickerValue.checkout}
      code={datePickerValue.code}
      onChangeDates={onChangeDates}
      onClosePopover={onClosePopover}
      onChangeCode={onChangeCode}
      onClearPromoCode={onClearPromoCode}
      onClickSearch={onClickSearch}
    />
  );
};

export default observer(PromoCode);
